// src/storage/essayAttemptHelpers.js
//
// Pure, framework-free helpers for persisted essay attempts (Scope B). JSX-free so
// they unit-test under `node --test`. The persist writer + list hook live in
// useEssayAttempts.js; the My Essays panel renders the sorted list.

// Defensive caps. essayText matches the gradeEssay input cap (aiConfig
// gradeMaxEssayChars). The CF already bounds criteria (<=8) / improvements (<=3) /
// field chars (<=1500), but rules can't reach into nested arrays, so we re-cap here
// as a client-side belt-and-braces before the owner write.
import { storedFeedbackText } from '../utils/feedbackText.js';

export const MAX_ESSAY_CHARS = 20000;
const MAX_ARRAY = 500;
const MAX_FIELD_CHARS = 4000;

function str(v, max = MAX_FIELD_CHARS) {
    return String(v == null ? '' : v).slice(0, max);
}

// Shape a SUCCESSFUL gradeEssay result (status === 'graded') into the persisted
// aiFeedback subset: drops the transport `status`, keeps only the allowlisted
// feedback fields, and bounds every array/string. mostLikelyBand is optional and
// only included when present (matches the rules + the grade contract).
export function buildAiFeedback(graded) {
    const g = graded || {};
    const range = g.indicativeBandRange || {};
    const fb = {
        indicativeBandRange: { low: str(range.low, 60), high: str(range.high, 60) },
        confidence: str(g.confidence, 20),
        criteria: (Array.isArray(g.criteria) ? g.criteria : []).slice(0, MAX_ARRAY).map((c) => ({
            name: storedFeedbackText(c && c.name, 200),
            comment: storedFeedbackText(c && c.comment, MAX_FIELD_CHARS),
            evidenceFromEssay: storedFeedbackText(c && c.evidenceFromEssay, MAX_FIELD_CHARS),
        })),
        improvements: (Array.isArray(g.improvements) ? g.improvements : [])
            .slice(0, MAX_ARRAY).map((s) => storedFeedbackText(s, MAX_FIELD_CHARS)),
        disclaimer: str(g.disclaimer, MAX_FIELD_CHARS),
    };
    if (g.mostLikelyBand != null) fb.mostLikelyBand = str(g.mostLikelyBand, 60);
    for (const k of ['assessmentProfileId', 'assessmentLabel', 'assessmentNote']) {
        if (typeof g[k] === 'string' && g[k].trim()) fb[k] = str(g[k], k === 'assessmentNote' ? 1000 : 100);
    }
    // Server-computed marks contract (per-pack config): the self-describing
    // aoMarks array + totals, persisted only when the CF supplied them (band-only
    // results simply omit them, so historical records are untouched and render
    // as before). Bounded per entry; the whole array counts as ONE rules key.
    if (Array.isArray(g.aoMarks) && g.aoMarks.length > 0) {
        fb.aoMarks = g.aoMarks.slice(0, 8).map((a) => ({
            key: str(a && a.key, 20),
            label: str(a && a.label, 60),
            mark: Number.isInteger(a && a.mark) ? a.mark : 0,
            maxMark: Number.isInteger(a && a.maxMark) ? a.maxMark : 0,
            level: Number.isInteger(a && a.level) ? a.level : 0,
        }));
    }
    // totalMark/totalMax for the config contract; the flat ao4/ao5 fields are
    // legacy tolerance for the interim 45-mark contract (mixed-version window).
    for (const k of ['ao4Mark', 'ao4Level', 'ao5Mark', 'ao5Level', 'totalMark', 'totalMax']) {
        if (Number.isInteger(g[k])) fb[k] = g[k];
    }
    if (typeof g.profileNote === 'string' && g.profileNote.trim() !== '') {
        fb.profileNote = str(g.profileNote, MAX_FIELD_CHARS);
    }
    if (g.gradingMaterials?.version === 1) {
        const m = g.gradingMaterials;
        fb.gradingMaterials = {
            version: 1, packId: str(m.packId, 120), packTitle: str(m.packTitle, 160), note: str(m.note, 1200),
            sources: (Array.isArray(m.sources) ? m.sources : []).slice(0, 12)
                .filter(s => s && ['original_text', 'teaching_notes', 'reference_model', 'mark_scheme', 'question_paper', 'exam_specification', 'editorial_context'].includes(s.kind))
                .map(s => ({ packId: str(s.packId, 120), itemId: str(s.itemId, 200),
                    scope: s.scope === 'paidItems' ? 'paidItems' : 'freeItems', title: str(s.title, 160),
                    kind: s.kind, partial: s.partial === true, revision: str(s.revision, 16), sourceDatabase: str(s.sourceDatabase, 50),
                    ...(s.sourceId ? { sourceId: str(s.sourceId, 32), sourcePartId: str(s.sourcePartId, 200),
                        edition: str(s.edition, 300), range: str(s.range, 1000), hash: str(s.hash, 64),
                        binding: str(s.binding, 10), reviewStatus: str(s.reviewStatus, 10),
                        examBoard: str(s.examBoard, 100), qualification: str(s.qualification, 100), paper: str(s.paper, 100),
                        session: str(s.session, 150), questionRange: str(s.questionRange, 300), paperKind: str(s.paperKind, 10),
                        taskMatch: str(s.taskMatch, 50) } : {}) })),
        };
    }
    return fb;
}

// Build the full essayAttempt doc body (minus takenAt, which the writer stamps with
// serverTimestamp()). Returns null when the inputs can't form a valid record (no
// pack/prompt, no graded result) so the writer can no-op cleanly.
export function buildAttemptDoc({ packId, promptId, promptTitle, essayText, graded }) {
    if (!packId || !promptId) return null;
    if (!graded || graded.status !== 'graded') return null;
    const body = {
        packId: str(packId, 120),
        promptId: str(promptId, 200),
        essayText: str(essayText, MAX_ESSAY_CHARS),
        aiFeedback: buildAiFeedback(graded),
    };
    const title = str(promptTitle, 200).trim();
    if (title) body.promptTitle = title;
    return body;
}

// Build an OWN-ESSAY attempt doc (own-essay-ai-grading slice): the user's own
// question graded against an entitled pack's rubric. Satisfies the rules'
// required base fields (packId = the rubric pack; promptId = the constant
// 'own') and adds the widened optional fields: source:'own', promptText (the
// full question, <= 6000 per rules/CF), rubricPackId, packLabel. promptTitle
// carries a 200-char question snippet so history rows title themselves.
export function buildOwnAttemptDoc({ rubricPackId, packLabel, promptText, essayText, graded }) {
    if (!rubricPackId) return null;
    if (!graded || graded.status !== 'graded') return null;
    const question = str(promptText, 6000).trim();
    if (!question) return null;
    const body = {
        packId: str(rubricPackId, 120),
        promptId: 'own',
        source: 'own',
        promptText: question,
        rubricPackId: str(rubricPackId, 120),
        promptTitle: question.slice(0, 200),
        essayText: str(essayText, MAX_ESSAY_CHARS),
        aiFeedback: buildAiFeedback(graded),
    };
    const label = str(packLabel, 200).trim();
    if (label) body.packLabel = label;
    return body;
}

// Newest-first sort for the My Essays history list. Tolerates Firestore Timestamps
// (toMillis/seconds), Date, ms numbers, and missing values (sorted last).
function takenAtMillis(a) {
    const t = a && a.takenAt;
    if (!t) return 0;
    if (typeof t.toMillis === 'function') return t.toMillis();
    if (typeof t.seconds === 'number') return t.seconds * 1000;
    if (t instanceof Date) return t.getTime();
    const n = Number(t);
    return Number.isFinite(n) ? n : 0;
}

export function sortAttemptsNewestFirst(attempts) {
    return (Array.isArray(attempts) ? attempts.slice() : [])
        .sort((a, b) => takenAtMillis(b) - takenAtMillis(a));
}
